import React, { useState, useEffect, useRef } from 'react';
import { VscBug, VscCommentDiscussion } from 'react-icons/vsc';
import { MdPrint } from 'react-icons/md';
import { changePassword } from '../../api/auth';
import { enablePush } from '../../utils/push';
import { submitFeedback } from '../../api/feedback';
import { getPrinterConfig, setPrinterConfig } from '../../api/printJobs';
import Avatar from '../common/Avatar';
import { useInstallState, IosInstructions } from '../common/InstallApp';
import { promptInstall } from '../../utils/install';
import api from '../../api/client';
import './Settings.css';

function getPushStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  return Notification.permission;
}

function NotificationSettings() {
  const [status, setStatus]   = useState(getPushStatus);
  const [working, setWorking] = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');

  const handleEnable = async () => {
    setWorking(true); setMsg(''); setErr('');
    try {
      await enablePush();
      setStatus('granted');
      setMsg('Notifications enabled successfully.');
    } catch (e) {
      setErr(e.message || 'Could not enable notifications.');
      setStatus(getPushStatus());
    } finally {
      setWorking(false);
    }
  };

  if (status === 'unsupported') {
    return (
      <div className="settings-card">
        <div className="settings-section-title">Push Notifications</div>
        <p className="settings-error" style={{ marginTop: 0 }}>
          Push notifications are not supported on this browser.
          On iOS, add this page to your home screen first.
        </p>
      </div>
    );
  }

  return (
    <div className="settings-card">
      <div className="settings-section-title">Push Notifications</div>
      <div className="settings-notif-row">
        <span className="settings-notif-label">Status:</span>
        <span className={`settings-notif-status notif-${status}`}>
          {status === 'granted' ? 'Enabled' : status === 'denied' ? 'Blocked' : 'Not enabled'}
        </span>
      </div>
      {status !== 'granted' && status !== 'denied' && (
        <button className="btn-primary" style={{ marginTop: 12 }} onClick={handleEnable} disabled={working}>
          {working ? 'Enabling…' : 'Enable Notifications'}
        </button>
      )}
      {status === 'denied' && (
        <p className="settings-error" style={{ marginTop: 8 }}>
          Notifications are blocked. Please allow them in your browser/OS settings and reload.
        </p>
      )}
      {err && <p className="settings-error" style={{ marginTop: 8 }}>{err}</p>}
      {msg && <p className="settings-success" style={{ marginTop: 8 }}>{msg}</p>}
      {status === 'granted' && !msg && (
        <p className="settings-hint">You will be notified for direct messages and @mentions.</p>
      )}
    </div>
  );
}

function InstallSettings() {
  const { installed, canPrompt, ios } = useInstallState();
  return (
    <div className="settings-card">
      <div className="settings-section-title">Install App</div>
      {installed ? (
        <p className="settings-hint" style={{ marginTop: 0 }}>KSEPro is installed on this device.</p>
      ) : canPrompt ? (
        <>
          <p className="settings-hint" style={{ marginTop: 0, marginBottom: 12 }}>
            Install KSEPro as an app for quick access and notifications.
          </p>
          <button className="btn-primary" onClick={() => promptInstall()}>Install App</button>
        </>
      ) : ios ? (
        <p className="settings-hint" style={{ marginTop: 0 }}>
          <IosInstructions /> Notifications on iPhone/iPad only work once the app is added to the home screen.
        </p>
      ) : (
        <p className="settings-hint" style={{ marginTop: 0 }}>
          Open your browser's menu and choose <b>Install app</b> or <b>Add to Home screen</b>.
        </p>
      )}
    </div>
  );
}

function PrinterSettings() {
  const [all, setAll]         = useState([]);
  const [enabled, setEnabled] = useState([]);  // [{ name, displayName }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  useEffect(() => {
    getPrinterConfig()
      .then(r => {
        setAll(r.data.all);
        // normalise: old config may have plain strings
        const raw = r.data.enabled || [];
        setEnabled(raw.map(e => typeof e === 'string' ? { name: e, displayName: '' } : e));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isEnabled = (name) => enabled.some(e => e.name === name);

  const toggle = (name) => {
    setEnabled(prev =>
      isEnabled(name)
        ? prev.filter(e => e.name !== name)
        : [...prev, { name, displayName: '' }]
    );
  };

  const setDisplayName = (name, displayName) => {
    setEnabled(prev => prev.map(e => e.name === name ? { ...e, displayName } : e));
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await setPrinterConfig(enabled);
      const raw = res.data.enabled || [];
      setEnabled(raw.map(e => typeof e === 'string' ? { name: e, displayName: '' } : e));
      setMsg('Saved.');
    } catch { setMsg('Failed to save.'); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 3000); }
  };

  if (loading) return (
    <div className="settings-card">
      <div className="settings-section-title"><MdPrint size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Printers</div>
      <p className="settings-hint">Loading…</p>
    </div>
  );

  return (
    <div className="settings-card">
      <div className="settings-section-title"><MdPrint size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Printers</div>
      {all.length === 0 ? (
        <p className="settings-hint">No printers reported by the print agent. Make sure the agent is running.</p>
      ) : (
        <>
          <p className="settings-hint" style={{ marginBottom: 8 }}>Select which printers are available to all users. Optionally set a display name that users will see instead of the raw printer name.</p>
          <div className="settings-printer-list">
            {all.map(name => {
              const checked = isEnabled(name);
              const entry   = enabled.find(e => e.name === name);
              return (
                <div key={name} className="settings-printer-item">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(name)} />
                    <span className="settings-printer-name">{name}</span>
                  </label>
                  {checked && (
                    <input
                      className="settings-printer-displayname"
                      type="text"
                      placeholder="Display name (optional)"
                      value={entry?.displayName || ''}
                      onChange={e => setDisplayName(name, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            {msg && <span className={msg === 'Saved.' ? 'settings-success' : 'settings-error'}>{msg}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function ContactSettings() {
  const empty = { dateOfBirth: '', email: '', phone: '', emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelation: '' };
  const [form, setForm]     = useState(empty);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');

  useEffect(() => {
    api.get('/users/me')
      .then(r => setForm({
        ...Object.fromEntries(Object.keys(empty).map(k => [k, r.data[k] || ''])),
        dateOfBirth: r.data.dateOfBirth ? new Date(r.data.dateOfBirth).toISOString().slice(0, 10) : '',
      }))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const field = (key) => ({ value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(''); setErr('');
    setSaving(true);
    try {
      await api.put('/users/me/profile', form);
      setMsg('Personal information updated.');
    } catch (e2) {
      setErr(e2.response?.data?.msg || 'Failed to update personal information.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-card">
      <div className="settings-section-title">Personal Information</div>
      <form onSubmit={handleSubmit} className="settings-form">
        <label className="settings-label">
          Date of Birth
          <input className="settings-input" type="date" {...field('dateOfBirth')} />
        </label>
        <label className="settings-label">
          Email
          <input className="settings-input" type="email" {...field('email')} />
        </label>
        <label className="settings-label">
          Phone
          <input className="settings-input" type="tel" {...field('phone')} />
        </label>
        <div className="settings-section-title" style={{ marginTop: 6, marginBottom: 0 }}>Emergency Contact</div>
        <label className="settings-label">
          Name
          <input className="settings-input" type="text" {...field('emergencyContactName')} />
        </label>
        <label className="settings-label">
          Phone
          <input className="settings-input" type="tel" {...field('emergencyContactPhone')} />
        </label>
        <label className="settings-label">
          Relation
          <input className="settings-input" type="text" placeholder="e.g. spouse, parent" {...field('emergencyContactRelation')} />
        </label>
        {err && <p className="settings-error">{err}</p>}
        {msg && <p className="settings-success">{msg}</p>}
        <button className="btn-primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save Personal Information'}
        </button>
      </form>
    </div>
  );
}

function Settings({ currentUser, role, onUserUpdate }) {
  const [cur, setCur]     = useState('');
  const [next, setNext]   = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [theme, setTheme]       = useState(currentUser?.theme    || 'dark');
  const [fontSize, setFontSize] = useState(currentUser?.fontSize || 'normal');

  const handleTheme = async (newTheme) => {
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    onUserUpdate?.({ theme: newTheme });
    try { await api.put('/users/me/profile', { theme: newTheme }); } catch {}
  };

  const handleFontSize = async (newSize) => {
    setFontSize(newSize);
    if (newSize !== 'normal') document.documentElement.setAttribute('data-fontsize', newSize);
    else document.documentElement.removeAttribute('data-fontsize');
    onUserUpdate?.({ fontSize: newSize });
    try { await api.put('/users/me/profile', { fontSize: newSize }); } catch {}
  };

  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [dnMsg, setDnMsg]   = useState('');
  const [dnErr, setDnErr]   = useState('');
  const [dnSaving, setDnSaving] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [avatarUser, setAvatarUser] = useState(currentUser);
  const fileRef = useRef(null);

  useEffect(() => {
    setDisplayName(currentUser?.displayName || '');
    setAvatarUser(currentUser);
  }, [currentUser?.id]);

  const handlePassword = async (e) => {
    e.preventDefault();
    setPwMsg(''); setPwErr('');
    if (next.length < 6) return setPwErr('New password must be at least 6 characters.');
    setSaving(true);
    try {
      await changePassword({ currentPassword: cur, newPassword: next });
      setPwMsg('Password updated.');
      setCur(''); setNext('');
    } catch (err) {
      setPwErr(err.response?.data?.msg || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisplayName = async (e) => {
    e.preventDefault();
    setDnMsg(''); setDnErr('');
    setDnSaving(true);
    try {
      await api.put('/users/me/profile', { displayName });
      setDnMsg('Display name updated.');
      onUserUpdate?.({ displayName });
    } catch (err) {
      setDnErr(err.response?.data?.msg || 'Failed to update display name.');
    } finally {
      setDnSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser?.id) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await api.post(`/users/${currentUser.id}/profile-image`, form);
      const profileImage = res.data.profileImage;
      setAvatarUser(u => ({ ...u, profileImage }));
      onUserUpdate?.({ profileImage });
    } catch {}
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div className="settings-wrap">
      <h2 className="settings-title">Settings</h2>
      <div className="settings-grid">

      {/* Profile */}
      <div className="settings-card">
        <div className="settings-section-title">Profile</div>
        <div className="settings-avatar-row">
          <Avatar user={avatarUser} size={56} />
          <div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
            <button className="btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Change Picture'}
            </button>
            <p className="settings-hint" style={{ marginTop: 4 }}>JPG or PNG, max 5 MB</p>
          </div>
        </div>
        <form onSubmit={handleDisplayName} className="settings-form" style={{ marginTop: 16 }}>
          <label className="settings-label">
            Display Name
            <input className="settings-input" type="text" value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Full name shown in the app" />
          </label>
          <p className="settings-hint" style={{ marginTop: -6 }}>
            Shown instead of your username in chat and user lists.
          </p>
          {dnErr && <p className="settings-error">{dnErr}</p>}
          {dnMsg && <p className="settings-success">{dnMsg}</p>}
          <button className="btn-primary" type="submit" disabled={dnSaving}>
            {dnSaving ? 'Saving…' : 'Save Display Name'}
          </button>
        </form>
      </div>

      <ContactSettings />

      {/* Appearance */}
      <div className="settings-card">
        <div className="settings-section-title">Appearance</div>
        <div className="settings-theme-row">
          <button className={`settings-theme-btn${theme === 'dark'  ? ' active' : ''}`} onClick={() => handleTheme('dark')}>Dark</button>
          <button className={`settings-theme-btn${theme === 'light' ? ' active' : ''}`} onClick={() => handleTheme('light')}>Light</button>
        </div>
        <div className="settings-section-title" style={{ marginTop: 16 }}>Text Size</div>
        <div className="settings-theme-row">
          <button className={`settings-theme-btn${fontSize === 'normal' ? ' active' : ''}`} onClick={() => handleFontSize('normal')}>Normal</button>
          <button className={`settings-theme-btn${fontSize === 'large'  ? ' active' : ''}`} onClick={() => handleFontSize('large')}>Large</button>
          <button className={`settings-theme-btn${fontSize === 'xlarge' ? ' active' : ''}`} onClick={() => handleFontSize('xlarge')}>Extra large</button>
        </div>
      </div>

      {/* Password */}
      <div className="settings-card">
        <div className="settings-section-title">Change Password</div>
        <form onSubmit={handlePassword} className="settings-form">
          <label className="settings-label">
            Current Password
            <input className="settings-input" type="password" value={cur} onChange={e => setCur(e.target.value)} required />
          </label>
          <label className="settings-label">
            New Password
            <input className="settings-input" type="password" value={next} onChange={e => setNext(e.target.value)} required />
          </label>
          {pwErr && <p className="settings-error">{pwErr}</p>}
          {pwMsg && <p className="settings-success">{pwMsg}</p>}
          <button className="btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Update Password'}</button>
        </form>
      </div>

      {['admin', 'engineer', 'manager'].includes(role) && <PrinterSettings />}
      <NotificationSettings />
      <InstallSettings />
      <FeedbackForm />
      </div>
    </div>
  );
}

function FeedbackForm() {
  const [type, setType]       = useState('feedback');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [err, setErr]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true); setErr(''); setSent(false);
    try {
      await submitFeedback({ type, message: message.trim() });
      setSent(true); setMessage('');
    } catch {
      setErr('Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="settings-card">
      <div className="settings-section-title">Bug Report / Feedback</div>
      <form onSubmit={handleSubmit} className="settings-form">
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button type="button"
            className={`btn-secondary${type === 'bug' ? ' active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => setType('bug')}>
            <VscBug size={14} /> Bug Report
          </button>
          <button type="button"
            className={`btn-secondary${type === 'feedback' ? ' active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => setType('feedback')}>
            <VscCommentDiscussion size={14} /> Feedback
          </button>
        </div>
        <label className="settings-label">
          {type === 'bug' ? 'Describe the bug — what happened and what you expected' : 'Your feedback'}
          <textarea className="settings-input" rows={4}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
            value={message} onChange={e => setMessage(e.target.value)} required />
        </label>
        {err  && <p className="settings-error">{err}</p>}
        {sent && <p className="settings-success">Sent — thank you!</p>}
        <button className="btn-primary" type="submit" disabled={sending || !message.trim()}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default Settings;
